import modelRecords from './spacecraftModels.json';
import type { Mission } from '../types';

// Empty identity lists are still string arrays, not a separate never[] variant.
export type SpacecraftReference = Omit<(typeof modelRecords)[number], 'noradIds' | 'missionIds'> & {
  noradIds: string[];
  missionIds: string[];
};
const models: SpacecraftReference[] = modelRecords;
export const EXPANDED_SPACECRAFT_MODELS = models;

/** Mission keys take precedence over legacy catalogues with conflicting NORAD numbers.
 * Live feeds may use a documented family-name match; that remains a family reference.
 */
export function spacecraftReferenceFor(
  mission: Pick<Mission, 'id' | 'name' | 'noradId'>,
): SpacecraftReference | undefined {
  const exact = models.find((m) => m.missionIds.includes(mission.id));
  if (exact) {
    // Known identities must agree. Empty lists deliberately avoid certifying unreviewed IDs.
    if (
      exact.noradIds.length &&
      mission.noradId != null &&
      !exact.noradIds.includes(String(mission.noradId))
    ) {
      // Hosted instruments are represented by the host, not assigned the host's orbit.
      if (exact.id === 'iss' && mission.id !== 'iss') return exact;
      return undefined;
    }
    return exact;
  }
  // Only feed IDs can fall back to a NORAD match. Static programme rows may have stale IDs.
  const live = /^(catalog|station|gnss|sentinel|oneweb|starlink|satnogs|aws)_\d+$/.test(mission.id);
  if (live && mission.noradId != null) {
    const identity = models.find((m) => m.noradIds.includes(String(mission.noradId)));
    if (identity) return identity;
  }
  const name = mission.name.trim();
  // Debris, rocket bodies and released objects are never the parent spacecraft.
  if (/\b(DEB|DEBRIS|R\/B|OBJECT [A-Z]|TOOLBAG)\b/i.test(name)) return undefined;
  if (live && /^SENTINEL-6\b/i.test(name)) return models.find((m) => m.id === 'sentinel6');
  if (/^(ISS|ISS \(ZARYA\))$/i.test(name)) return models.find((m) => m.id === 'iss');
  if (live && /^gnss_/.test(mission.id) && /^COSMOS\b/i.test(name))
    return models.find((m) => m.id === 'glonass');
  if (live && /^(oneweb|starlink)_/.test(mission.id))
    return models.find((m) => m.id === mission.id.split('_')[0]);
  return live ? models.find((m) => m.pattern && new RegExp(m.pattern, 'i').test(name)) : undefined;
}
